import React from "react";

// react router
import { useParams } from "react-router-dom";

// components
// import RequestStep from "../misc/RequestStep";
import { EditPrice } from "../misc/EditPrice";
import ViewDocument from "../misc/ViewDocument";
import Button from "../../../component/element/forms/Button";
import { UploadDocument } from "../misc/UploadDocument";

// icons
import {
  FaNairaSign,
  FaLocationDot,
  FaCalendarDays,
  FaPlus,
  FaFileLines,
} from "react-icons/fa6";

// utils
import {
  real_date,
  real_date_time,
  format_date_time,
} from "../../../services/convert/date";

// state and service
import {
  add_request_price,
  cancel_request,
  get_request,
  update_request_price,
  upload_document,
} from "../../../services/apis/requests";
import { useAppDispatch, useAppSelector } from "../../../store";
import { IPrice } from "../../../store/types/request";
import { SectionCard } from "../misc/SectionCard";
import { Pill } from "../misc/Pill";
import { MetaChip } from "../misc/MetaChip";
import { InfoRow } from "../misc/InfoRow";
import { EmptyState } from "../misc/EmptyState";
import { PriceCard } from "../misc/PriceCard";
import { FileText } from "lucide-react";

const ShowRequest = () => {
  const { id } = useParams();
  const dispatch = useAppDispatch();
  const request = useAppSelector(({ request }) => request.request);

  const [showPriceEdit, setShowPriceEdit] = React.useState(false);
  const [showDocument, setShowDocument] = React.useState(false);
  const [showUpload, setShowUpload] = React.useState(false);
  const [editingPrice, setEditingPrice] = React.useState<IPrice | null>(null);

  // Fixed: Create a copy before sorting to avoid mutation
  const sortPrices = (prices: IPrice[]) => {
    return [...prices].sort((a, b) => {
      const dateA = new Date(a.created_at || "").getTime();
      const dateB = new Date(b.created_at || "").getTime();
      return dateB - dateA;
    });
  };

  // Option 1: Memoize the sorted prices to avoid re-sorting on every render
  const sortedPrices = React.useMemo(() => {
    return sortPrices(request?.price || []);
  }, [request?.price]);

  const latestPrice = sortedPrices[0];

  const onLoad = React.useCallback(() => {
    if (id) get_request(dispatch, id);
  }, [dispatch, id]);

  React.useEffect(() => {
    onLoad();
  }, [onLoad]);

  const openCreatePriceModal = () => {
    setEditingPrice(null);
    setShowPriceEdit(true);
  };

  const openEditPriceModal = (price: IPrice) => {
    setEditingPrice(price);
    setShowPriceEdit(true);
  };

  return (
    <div className="w-full max-w-6xl mx-auto grid gap-4">
      {/* ── Header ── */}
      <div className="flex flex-col md:flex-row items-start justify-between gap-4">
        <div className="flex items-center gap-4">
          {/* Service image — rectangular logo treatment, not avatar */}
          <div className="w-[72px] h-[72px] flex-shrink-0 rounded-xl border-2 border-gray-200 bg-gray-50 flex items-center justify-center overflow-hidden">
            {request?.user?.profile?.avatar ? (
              <img
                src={request?.user?.profile?.avatar}
                alt={request?.user?.profile?.name}
                className="w-full h-full object-cover"
              />
            ) : (
              <FaFileLines size={28} className="text-gray-300" />
            )}
          </div>

          <div>
            <div className="flex items-center gap-2 mb-1.5">
              <h2 className="text-xl font-bold text-gray-800 m-0">
                {request?.user?.profile.name}
              </h2>
              {request?.type && (
                <Pill
                  label={
                    request.type.charAt(0).toUpperCase() + request.type.slice(1)
                  }
                  color="blue"
                />
              )}
            </div>
            <div className="text-sm text-gray-500">{request?.user?.email}</div>
          </div>
        </div>

        <div className="w-full md:w-auto grid grid-cols-2 md:flex gap-2 md:flex-shrink-0">
          <Button
            title="Cancel"
            type="button"
            onClick={() => cancel_request(dispatch)}
            className="flex items-center gap-1.5 bg-white hover:!bg-red-50 text-red-500 border !border-red-200 text-sm"
          />
          <Button
            title="Upload Document"
            type="button"
            onClick={() => setShowUpload(true)}
            className="flex items-center gap-1.5 bg-primary-500 text-white text-sm"
          />
        </div>
      </div>

      {/* ── Hero image ── */}
      <div className="relative w-full max-h-72 overflow-hidden border-b border-gray-200/20 rounded-2xl shadow-sm">
        {request?.service?.image ? (
          <img
            loading="lazy"
            src={request?.service?.image}
            alt={request?.service?.name}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-48 flex items-center justify-center bg-gray-100 rounded-lg">
            <FileText size={48} className="text-gray-300" />
          </div>
        )}

        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent" />
        {/* Title overlay */}
        <div className="flex items-center justify-between absolute bottom-0 left-0 right-0 px-4 pb-3">
          <div>
            <h1 className="text-xl font-semibold text-white mb-0.5">
              {request?.service?.name}
            </h1>
            <div className="flex items-center gap-2 flex-wrap">
              {request?.created_at && (
                <MetaChip
                  icon={<FaCalendarDays />}
                  label={real_date_time(request.created_at)}
                />
              )}
              {request?.state?.name && (
                <MetaChip
                  icon={<FaLocationDot />}
                  label={`${request.state.name}, ${request.state.country?.name}`}
                />
              )}
            </div>
          </div>
          <span
            className={`inline-flex items-center gap-1.5 text-[12px] font-semibold px-3 py-1.5 rounded-full tracking-wide  ${request?.service?.status === "1" || request?.service?.status === 1 ? "bg-primary-100/50 text-primary-500" : "bg-red-100/30 text-red-500"} backdrop-blur-sm `}
          >
            <span
              className={`w-1.5 h-1.5 rounded-full ${request?.service?.status === "1" || request?.service?.status === 1 ? "bg-primary-500" : "bg-red-500"}`}
            />
            {request?.service?.status === "1" || request?.service?.status === 1
              ? "Active"
              : "Inactive"}
          </span>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        {/* Left grid content */}
        <div className="lg:col-span-2 flex flex-col gap-4">
          {/* Service or Document details */}
          <SectionCard
            title={request?.service ? "Service Info" : "Document"}
            className="w-full"
          >
            {request?.service ? (
              <>
                <InfoRow label="Name" value={request.service.name} />
                {request.service.description && (
                  <>
                    <p
                      className="text-base text-gray-500 leading-relaxed"
                      dangerouslySetInnerHTML={{
                        __html: request.service.description,
                      }}
                    />
                  </>
                )}
                {request.service.packages?.length > 0 && (
                  <>
                    <p className="text-[10px] uppercase tracking-widest text-gray-400 pt-3 border-t border-gray-100 mb-2">
                      Packages
                    </p>
                    <div className="space-y-1">
                      {request.service.packages.map((pkg, index) => (
                        <div
                          key={
                            typeof pkg === "string" ? `${pkg}-${index}` : pkg.id
                          }
                          className="text-sm capitalize text-gray-700 py-1.5 border-b border-gray-100 last:border-b-0"
                        >
                          {typeof pkg === "string" ? pkg : pkg.name}
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </>
            ) : request?.document ? (
              <div className="space-y-0">
                <InfoRow label="Name" value={request.document.name} />
                <InfoRow
                  label="Issuing body"
                  value={request.document.issuing_body}
                />
                <InfoRow
                  label="Issued date"
                  value={
                    request.document.issuing_date
                      ? format_date_time(request.document.issuing_date)
                      : undefined
                  }
                />
                <InfoRow
                  label="Expiry date"
                  value={
                    request.document.expiring_date
                      ? format_date_time(request.document.expiring_date)
                      : undefined
                  }
                />
              </div>
            ) : (
              <EmptyState
                icon={<FaFileLines />}
                label="No service or document attached"
              />
            )}
          </SectionCard>

          {/* Additional notes */}
          <SectionCard title="Additional notes">
            {request?.others ? (
              <div
                className="text-sm text-gray-700 leading-relaxed bg-gray-50 rounded-lg mb-2"
                dangerouslySetInnerHTML={{ __html: request.others }}
              />
            ) : (
              <EmptyState icon="📝" label="No additional notes" />
            )}

            <div className="flex items-center justify-between pt-3 border-t border-gray-100 ">
              <span className="text-xs text-gray-400">Attached document</span>
              {request?.document ? (
                <button
                  onClick={() => setShowDocument(true)}
                  className="flex items-center gap-1.5 text-xs text-primary-500 hover:underline"
                >
                  <FaFileLines size={11} /> View file
                </button>
              ) : (
                <Pill label="None" color="gray" />
              )}
            </div>
          </SectionCard>
        </div>

        {/* Right grid content */}
        <div className="flex flex-col gap-4">
          {/* Add Price */}
          <SectionCard
            title="Add Price"
            icon={<span className="text-sm">₦</span>}
          >
            <button
              onClick={openCreatePriceModal}
              className="w-full flex items-center justify-center gap-1.5 py-2 text-xs bg-primary-500 text-white rounded-lg hover:bg-primary-600"
            >
              <FaPlus size={11} /> Add price
            </button>
          </SectionCard>

          {/* Price */}
          <SectionCard title="Prices" icon={<span className="text-sm">₦</span>}>
            <div className="grid gap-3">
              {request?.price?.length ? (
                sortedPrices.map((item) => (
                  <PriceCard
                    key={item.id}
                    amount={Number(item.price)}
                    submitDate={
                      item.created_at ? real_date(item.created_at) : "—"
                    }
                    role={request.user?.role}
                    payment_link={item.payment_link}
                    onView={() =>
                      window.open(
                        item.payment_link || "",
                        "_blank",
                        "noopener,noreferrer",
                      )
                    }
                    onEdit={() => openEditPriceModal(item)}
                    isLatest={latestPrice?.id === item.id}
                    isAgreed={item.is_agreed}
                    status={item.status}
                    // admin_confirmed={item.agreed_admin_confirm_date}
                  />
                ))
              ) : (
                <EmptyState icon={<FaNairaSign />} label="No price added yet" />
              )}
            </div>
          </SectionCard>
        </div>
      </div>

      {/* ── Modals ── */}
      {request?.document && (
        <ViewDocument
          title={request.document.name || ""}
          path={request.document.path || ""}
          visible={showDocument}
          onCloseView={() => setShowDocument(false)}
        />
      )}

      {id && (
        <EditPrice
          id={id}
          visible={showPriceEdit}
          title={editingPrice ? "Update Price" : "Add Price"}
          requirePaymentLink={Boolean(editingPrice)}
          initialData={{
            price: editingPrice?.price || "",
            payment_link: editingPrice?.payment_link || "",
          }}
          onCloseView={(res) => {
            if (res.request_id && res.price) {
              if (editingPrice?.id) {
                update_request_price(dispatch, {
                  price_id: editingPrice.id,
                  price: res.price,
                  payment_link: res.payment_link?.trim() ?? "",
                });
              } else {
                add_request_price(dispatch, res);
              }
            }
            setEditingPrice(null);
            setShowPriceEdit(false);
          }}
        />
      )}

      <UploadDocument
        show={showUpload}
        onCloseView={(res) => {
          if (res.request_id && res.path) upload_document(dispatch, res);
          setShowUpload(false);
        }}
      />
    </div>
  );
};

export default ShowRequest;
